/**
 * ReservoirHealthScorecard.tsx - Six-dimensional health visualisation for a
 *                                reservoir scenario.  Drops onto the Train
 *                                Summary page (and embeddable elsewhere).
 *
 *   - Overall score 0-100 with traffic light + ring chart
 *   - Six dimension cards (Bed, Oxygen, Algae, Nutrients, Compliance, Ops)
 *   - Each card: score bar, 5 sub-metrics with status pills, contributing modules
 *   - Top 5 prioritised actions
 */

import { Heart, Layers, Droplets, Sparkles, Beaker, ShieldCheck, Wind,
  CheckCircle2, AlertTriangle, XCircle, ChevronDown, ChevronUp,
} from 'lucide-react';
import { useState } from 'react';
import { assessReservoirHealth, type HealthDimension, type HealthStatus, type HealthSubMetric } from '@/lib/reservoirHealth';
import type { ScenarioInputs } from '@/lib/scenarioManager';
import { MODULES, useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleInfoModal from '@/components/ModuleInfoModal';

const DIM_ICON: Record<string, typeof Heart> = {
  bed:         Layers,
  oxygen:      Droplets,
  algae:       Sparkles,
  nutrients:   Beaker,
  compliance:  ShieldCheck,
  operational: Wind,
};

const STATUS_COLOUR: Record<HealthStatus, { ring: string; bg: string; border: string; text: string; pill: string; }> = {
  ok:       { ring: 'ring-emerald-500',   bg: 'bg-emerald-900/15', border: 'border-emerald-700', text: 'text-emerald-300', pill: 'bg-emerald-900/40 text-emerald-200 border-emerald-700' },
  warn:     { ring: 'ring-amber-500',     bg: 'bg-amber-900/15',   border: 'border-amber-700',   text: 'text-amber-300',   pill: 'bg-amber-900/40 text-amber-200 border-amber-700' },
  critical: { ring: 'ring-red-500',       bg: 'bg-red-900/15',     border: 'border-red-700',     text: 'text-red-300',     pill: 'bg-red-900/40 text-red-200 border-red-700' },
};

interface Props {
  params: ScenarioInputs;
  scenarioName?: string;
}

export default function ReservoirHealthScorecard({ params, scenarioName }: Props) {
  const assessment = assessReservoirHealth(params);
  const overallC = STATUS_COLOUR[assessment.overallStatus];

  return (
    <section className={`rounded-2xl border-2 ${overallC.border} ${overallC.bg} p-4 space-y-4`}>
      {/* Overall header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Heart className={`w-5 h-5 ${overallC.text}`} />
            <h2 className="text-base font-bold text-slate-100">Reservoir Health Scorecard</h2>
            {scenarioName && (
              <span className="text-xs text-slate-400">- {scenarioName}</span>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-1 leading-snug">
            Six-dimensional assessment that quantifies what makes a reservoir healthy: bed condition, oxygen budget,
            algae &amp; T/O risk, nutrient regime, regulatory compliance, and operational aeration performance.
          </p>
        </div>
        <ScoreRing score={assessment.overallScore} status={assessment.overallStatus} />
      </div>

      {/* Dimension grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {assessment.dimensions.map(d => (
          <DimensionCard key={d.id} d={d} />
        ))}
      </div>

      {/* Top actions */}
      {assessment.topActions.length > 0 && (
        <div className="rounded-xl border border-amber-700/60 bg-amber-900/10 p-3">
          <div className="text-sm font-semibold text-amber-100 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Prioritised actions to improve reservoir health
          </div>
          <ol className="list-decimal ml-5 space-y-1 text-sm text-amber-50">
            {assessment.topActions.map((a, i) => <li key={i}>{a}</li>)}
          </ol>
        </div>
      )}
    </section>
  );
}

// ------------------------------------------------------------------------

function ScoreRing({ score, status }: { score: number; status: HealthStatus }) {
  const c = STATUS_COLOUR[status];
  const r = 26, circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  const stroke = status === 'ok' ? '#10b981' : status === 'warn' ? '#f59e0b' : '#ef4444';
  return (
    <div className="flex items-center gap-2">
      <div className="relative w-16 h-16">
        <svg width="64" height="64" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r={r} stroke="rgba(255,255,255,0.06)" strokeWidth="6" fill="none" />
          <circle
            cx="32" cy="32" r={r}
            stroke={stroke} strokeWidth="6" fill="none"
            strokeDasharray={`${dash} ${circ}`}
            strokeLinecap="round"
            transform="rotate(-90 32 32)"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
          <span className={`text-lg font-bold ${c.text}`}>{score.toFixed(0)}</span>
          <span className="text-[8px] uppercase tracking-wider text-slate-500">/ 100</span>
        </div>
      </div>
      <div className="text-right">
        <div className={`text-xs uppercase tracking-wider font-bold ${c.text}`}>
          {status === 'ok' ? 'Healthy' : status === 'warn' ? 'At risk' : 'Critical'}
        </div>
        <div className="text-[10px] text-slate-500">overall</div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------

function DimensionCard({ d }: { d: HealthDimension }) {
  const [open, setOpen] = useState(false);
  const c = STATUS_COLOUR[d.status];
  const Icon = DIM_ICON[d.id] ?? Heart;
  const { isEnabled } = useModulesEnabled();
  return (
    <div className={`rounded-xl border ${c.border} bg-slate-900 p-3`}>
      {/* Header row */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${c.text}`} />
          <div>
            <div className="text-sm font-bold text-slate-100">{d.label}</div>
            <div className="text-[10px] text-slate-500 leading-snug">{d.blurb}</div>
          </div>
        </div>
        <div className="flex-shrink-0 text-right">
          <div className={`text-xl font-bold ${c.text}`}>{d.score.toFixed(0)}</div>
          <div className={`text-[9px] uppercase tracking-wider font-bold ${c.text}`}>
            {d.status === 'ok' ? 'OK' : d.status === 'warn' ? 'Warn' : 'Critical'}
          </div>
        </div>
      </div>

      {/* Score bar */}
      <div className="mt-2 w-full h-1.5 rounded-full bg-slate-800 overflow-hidden">
        <div
          className={`h-full ${d.status === 'ok' ? 'bg-emerald-500' : d.status === 'warn' ? 'bg-amber-500' : 'bg-red-500'}`}
          style={{ width: `${d.score}%` }}
        />
      </div>

      {/* Sub-metrics - compact pills (always visible) */}
      <div className="mt-2 space-y-1">
        {d.subMetrics.map((m, i) => <SubMetricRow key={i} m={m} />)}
      </div>

      {/* Toggle: contributing modules / action */}
      <button onClick={() => setOpen(o => !o)}
        className="mt-2 inline-flex items-center gap-1 text-[10px] text-sky-300 hover:text-sky-200">
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {open ? 'Hide modules & action' : 'Show modules & action'}
      </button>
      {open && (
        <div className="mt-2 space-y-2 border-t border-slate-700/70 pt-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Contributing modules</div>
            <div className="flex flex-wrap gap-1">
              {d.contributingModules.map(mid => {
                const mod = MODULES.find(m => m.id === mid);
                const on = isEnabled(mid);
                if (!mod) return null;
                return (
                  <div key={mid} className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] ${on ? 'border-emerald-700 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
                    {on && <CheckCircle2 className="w-2.5 h-2.5" />}
                    <span>{mod.label}</span>
                    <ModuleInfoModal moduleId={mid} triggerLabel="info" />
                  </div>
                );
              })}
            </div>
          </div>
          {d.action && (
            <div className="rounded-md border border-amber-700/60 bg-amber-900/15 p-2 text-[11px] text-amber-100 leading-snug">
              <strong>Action:</strong> {d.action}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SubMetricRow({ m }: { m: HealthSubMetric }) {
  const c = STATUS_COLOUR[m.status];
  const Icon = m.status === 'ok' ? CheckCircle2 : m.status === 'warn' ? AlertTriangle : XCircle;
  return (
    <div className="flex items-center justify-between gap-2 text-[11px]">
      <div className="flex items-center gap-1 text-slate-400 truncate">
        <Icon className={`w-3 h-3 ${c.text} flex-shrink-0`} />
        <span className="truncate">{m.label}</span>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className={`font-mono font-semibold ${c.text}`}>{m.value}</span>
        <span className="text-[9px] text-slate-600 italic hidden md:inline">- {m.target}</span>
      </div>
    </div>
  );
}
